import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  CheckCircle,
  X,
  ArrowRight,
  Shield,
  Target,
  HelpCircle,
  Star,
  Bot,
} from "lucide-react";

const Pricing = () => {
  const plans = [
    {
      id: "reputation-management",
      name: "Reputation Management",
      icon: Shield,
      description: "Complete reputation monitoring and management for your business",
      price: 200,
      popular: false,
      features: [
        { text: "24/7 AI-powered monitoring", included: true },
        { text: "8+ platform coverage", included: true },
        { text: "Review response assistance", included: true },
        { text: "Monthly reports", included: true },
        { text: "Negative content suppression", included: true },
        { text: "Review generation campaigns", included: true },
        { text: "Dedicated account manager", included: true },
        { text: "Google Ads management", included: false },
        { text: "A/B testing (5000+ parameters)", included: false },
        { text: "Search result influencing", included: false },
      ],
      cta: "Get Started",
      ctaLink: "/checkout/reputation-management",
    },
    {
      id: "online-marketing",
      name: "Online Marketing Package",
      icon: Target,
      description: "Full-service digital marketing with AI-powered optimization",
      price: 500,
      popular: true,
      features: [
        { text: "Everything in Reputation Management", included: true },
        { text: "Google Platinum Partner services", included: true },
        { text: "AdSense optimization & management", included: true },
        { text: "AI A/B testing (5000+ parameters)", included: true },
        { text: "Search result influencing", included: true },
        { text: "Positive content creation", included: true },
        { text: "Competitor analysis", included: true },
        { text: "Custom marketing strategy", included: true },
        { text: "Weekly performance reports", included: true },
        { text: "Priority support", included: true },
      ],
      cta: "Book Consultation",
      ctaLink: "/book-consultation",
      requiresConsultation: true,
    },
  ];

  const faqs = [
    {
      question: "What platforms do you monitor?",
      answer: "We monitor all major review platforms including Google Reviews, Yelp, Avvo (for attorneys), Vitals and WebMD (for healthcare), Zillow (for real estate), Facebook, BBB, and 40+ additional platforms depending on your industry.",
    },
    {
      question: "How quickly will I see results?",
      answer: "Most clients see improvements in their online reputation within 30-60 days. Review monitoring begins immediately, and our AI agents start working on your reputation strategy from day one. Full results typically manifest within 3-6 months.",
    },
    {
      question: "What makes your AI-powered approach different?",
      answer: "Traditional reputation companies rely on manual monitoring. Our AI agents work 24/7, monitoring reviews in real-time, analyzing sentiment patterns, and providing instant alerts. We also use AI for A/B testing with 5000+ parameters to optimize your marketing campaigns.",
    },
    {
      question: "Can you remove negative reviews?",
      answer: "We can help remove reviews that violate platform guidelines (fake reviews, inappropriate content, etc.). For legitimate negative reviews, we focus on suppression strategies and helping you generate positive reviews to improve your overall rating.",
    },
    {
      question: "Why does the Online Marketing Package require a consultation?",
      answer: "The Online Marketing Package includes custom strategy development, Google Ads management, and advanced A/B testing. To deliver the best results, we need to understand your specific business goals, target audience, and competitive landscape through an initial consultation.",
    },
    {
      question: "What is Google Platinum Partner status?",
      answer: "Google Platinum Partner is a recognition from Google for agencies that demonstrate expertise in Google Ads and meet strict performance requirements. This means you get access to advanced features, dedicated Google support, and proven expertise in Google marketing.",
    },
    {
      question: "Is there a contract or commitment?",
      answer: "Our services are month-to-month with no long-term contracts required. However, reputation management is an ongoing process, and we recommend a minimum 3-6 month commitment to see meaningful results.",
    },
    {
      question: "Do you work with specific industries?",
      answer: "Yes! We have specialized expertise in healthcare (doctors, dentists), legal (attorneys on Avvo), real estate (Zillow), restaurants (Yelp), and general businesses (Google, Facebook). Our strategies are tailored to your specific industry.",
    },
  ];

  return (
    <div data-testid="pricing-page" className="pt-20">
      {/* Hero */}
      <section className="hero-gradient py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-[#F37021]/10 text-[#F37021] px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Star className="h-4 w-4" />
              Pricing
            </div>
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-foreground mb-6"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              Simple, Transparent{" "}
              <span className="gradient-text">Pricing</span>
            </h1>
            <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
              Choose the plan that fits your reputation management needs. 
              No hidden fees, no surprises.
            </p>
          </div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-20 section-light -mt-16">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <div className="grid md:grid-cols-2 gap-8">
            {plans.map((plan, index) => (
              <Card
                key={plan.id}
                data-testid={`pricing-card-${index}`}
                className={`relative card-hover ${
                  plan.popular
                    ? "border-[#F37021] shadow-xl"
                    : "border-border"
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-[#F37021] text-white text-sm font-medium px-4 py-1 rounded-full">
                    Most Popular
                  </div>
                )}
                <CardHeader className="text-center pb-4 pt-8">
                  <div className="w-16 h-16 rounded-xl bg-[#F37021]/10 flex items-center justify-center mx-auto mb-4">
                    <plan.icon className="h-8 w-8 text-[#F37021]" />
                  </div>
                  <CardTitle
                    className="text-2xl"
                    style={{ fontFamily: "Outfit, sans-serif" }}
                  >
                    {plan.name}
                  </CardTitle>
                  <p className="text-sm text-muted-foreground mt-2">{plan.description}</p>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="text-center mb-6">
                    <span className="text-5xl font-bold text-foreground">${plan.price}</span>
                    <span className="text-muted-foreground">/month</span>
                  </div>

                  <Link to={plan.ctaLink} className="block mb-6">
                    <Button
                      data-testid={`pricing-cta-${index}`}
                      className={`w-full rounded-full py-6 text-lg ${
                        plan.popular
                          ? "bg-[#F37021] hover:bg-[#D95F18] text-white"
                          : "bg-foreground hover:bg-foreground/90 text-background"
                      }`}
                    >
                      {plan.cta}
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                  </Link>

                  {plan.requiresConsultation && (
                    <p className="text-xs text-center text-muted-foreground mb-6">
                      Consultation required to customize your strategy
                    </p>
                  )}

                  <ul className="space-y-3">
                    {plan.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center gap-3">
                        {feature.included ? (
                          <CheckCircle className="h-5 w-5 text-[#F37021] flex-shrink-0" />
                        ) : (
                          <X className="h-5 w-5 text-muted-foreground/50 flex-shrink-0" />
                        )}
                        <span className={feature.included ? "text-foreground" : "text-muted-foreground/50"}>
                          {feature.text}
                        </span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* AI Advantage Note */}
          <div className="mt-12 p-6 bg-[#F37021]/10 rounded-2xl">
            <div className="flex items-center gap-4">
              <Bot className="h-12 w-12 text-[#F37021] flex-shrink-0" />
              <div>
                <p className="font-bold text-foreground text-lg">Powered by AI</p>
                <p className="text-muted-foreground">
                  Both plans include our AI-powered monitoring agents that work 24/7 to protect 
                  your reputation. The Online Marketing Package adds advanced AI A/B testing 
                  with 5000+ parameters for maximum marketing performance.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What's Included Comparison */}
      <section className="py-20 lg:py-28 section-muted">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <h2
              className="text-3xl sm:text-4xl font-bold text-foreground mb-6"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              What's Included
            </h2>
          </div>

          <div className="bg-card rounded-2xl border border-border overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-muted/50">
                  <th className="text-left py-4 px-6 font-semibold">Feature</th>
                  <th className="text-center py-4 px-6 font-semibold">Reputation<br/>Management</th>
                  <th className="text-center py-4 px-6 font-semibold text-[#F37021]">Online<br/>Marketing</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { feature: "24/7 AI Monitoring", rep: true, market: true },
                  { feature: "8+ Platforms Covered", rep: true, market: true },
                  { feature: "Review Response Assistance", rep: true, market: true },
                  { feature: "Monthly Reports", rep: true, market: "Weekly" },
                  { feature: "Negative Content Suppression", rep: true, market: true },
                  { feature: "Review Generation Campaigns", rep: true, market: true },
                  { feature: "Dedicated Account Manager", rep: true, market: true },
                  { feature: "Google Platinum Partner Services", rep: false, market: true },
                  { feature: "AdSense Optimization", rep: false, market: true },
                  { feature: "A/B Testing (5000+ parameters)", rep: false, market: true },
                  { feature: "Search Result Influencing", rep: false, market: true },
                  { feature: "Positive Content Creation", rep: false, market: true },
                  { feature: "Competitor Analysis", rep: false, market: true },
                ].map((row, index) => (
                  <tr key={index} className="border-b border-border/50">
                    <td className="py-4 px-6 font-medium">{row.feature}</td>
                    <td className="text-center py-4 px-6">
                      {typeof row.rep === "boolean" ? (
                        row.rep ? (
                          <CheckCircle className="h-5 w-5 text-[#F37021] mx-auto" />
                        ) : (
                          <X className="h-5 w-5 text-muted-foreground/50 mx-auto" />
                        )
                      ) : (
                        <span className="text-muted-foreground">{row.rep}</span>
                      )}
                    </td>
                    <td className="text-center py-4 px-6 bg-[#F37021]/5">
                      {typeof row.market === "boolean" ? (
                        row.market ? (
                          <CheckCircle className="h-5 w-5 text-[#F37021] mx-auto" />
                        ) : (
                          <X className="h-5 w-5 text-muted-foreground/50 mx-auto" />
                        )
                      ) : (
                        <span className="font-medium text-[#F37021]">{row.market}</span>
                      )}
                    </td>
                  </tr>
                ))}
                <tr className="bg-muted/30">
                  <td className="py-4 px-6 font-bold">Price</td>
                  <td className="text-center py-4 px-6 font-bold text-xl">$200/mo</td>
                  <td className="text-center py-4 px-6 font-bold text-xl text-[#F37021] bg-[#F37021]/5">$500/mo</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 lg:py-28 section-light">
        <div className="max-w-3xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-[#F37021]/10 text-[#F37021] px-4 py-2 rounded-full text-sm font-medium mb-6">
              <HelpCircle className="h-4 w-4" />
              FAQ
            </div>
            <h2
              className="text-3xl sm:text-4xl font-bold text-foreground mb-6"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              Frequently Asked Questions
            </h2>
          </div>

          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`}>
                <AccordionTrigger
                  data-testid={`faq-${index}`}
                  className="text-left font-semibold"
                >
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-28 bg-[#1A1A1A]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center">
          <h2
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            Ready to Protect Your Reputation?
          </h2>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto mb-10">
            Get a free consultation and see how we can help improve your online presence.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact">
              <Button
                data-testid="pricing-contact-btn"
                size="lg"
                className="bg-[#F37021] hover:bg-[#D95F18] text-white rounded-full px-10"
              >
                Free Consultation
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Pricing;
